import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, Input, OnDestroy } from '@angular/core';
import { API_STATUS, ApiEnvNames, SearchConfig } from '../../model/api.model';
import { DealerTransferBandsChartSchema } from '../../model/dealer-transfer-analysis.model';
import { DataTableSchema, DefaultTableSchema } from '../../model/dynamic-data-table.model';
import { FieldNames } from '../../model/form-fields.model';
import { INFO_TYPES, InfoFieldConfig, DEFAULT_PROPS, DynamicFormArray, FieldSelectedValue, FormFieldConfig } from '../../model/form.model';
import { ChartComponent } from '../../layout/chart/chart.component';
import { DynamicDataTableComponent } from '../../layout/dynamic-data-table/dynamic-data-table.component';
import { CommonService } from '../../services/common.service';

interface TransferRow {
  oldDealer?: string;
  newDealer?: string;
  totalAssocInOldDealer?: number | string;
  totalAssocInNewDealer?: number | string;
  totalAssocInBoth?: number | string;
  transferPercentage?: number | string | null;
}

const transferBands = ['0-10%', '10-20%', '20-30%', '30-40%', '40-50%', '50-60%', '60-70%', '70-80%', '80-90%', '90-100%'];

const createDateField = (fieldName: string, displayName: string, defaultValue: string): FormFieldConfig => ({
  fieldName,
  displayName,
  fieldType: 'date',
  defaultValue,
  selectedValue: (() => {
    const parts = String(defaultValue || '').split('/');
    const month = Number(parts[0]) || 1;
    const day = Number(parts[1]) || 1;
    const year = Number(parts[2]) || new Date().getFullYear();
    return new Date(year, month - 1, day);
  })(),
  disabled: false,
  required: true,
  min: new Date(2026, 0, 1),
  max: new Date(2027, 11, 31),
});

@Component({
  selector: 'app-dealer-transfer-summary',
  standalone: true,
  imports: [CommonModule, DynamicDataTableComponent, ChartComponent],
  templateUrl: './dealer-transfer-summary.component.html',
  styleUrl: './dealer-transfer-summary.component.scss',
})
export class DealerTransferSummaryComponent implements OnInit , OnDestroy {
  readonly props = DEFAULT_PROPS;
  public tableSchema: DataTableSchema;
  readonly chartSchema = { ...DealerTransferBandsChartSchema };
  readonly apiConfig: SearchConfig;
  readonly filterSchema: DynamicFormArray;
  transferRows: TransferRow[] = [];
  chartRows: Array<{ band: string; count: number; dealerDetails: TransferRow[] }> = [];
  public chartInfoFields: InfoFieldConfig[] = [];
  validationMessage = '';
  isLoading = false;
  private readonly commonService = inject(CommonService);
  @Input() globalPayloadSubject?: any;
  private globalpayloadSub?: any;

  constructor() {
    this.tableSchema = {
      ...structuredClone(DefaultTableSchema),
      tableTitle: 'Dealer Transfer Report',
      dataSource: [],
      columnSchema: [
        { label: 'Old Dealer', fieldKey: 'oldDealer' },
        { label: 'New Dealer', fieldKey: 'newDealer' },
        { label: 'Old Associates Count', fieldKey: 'totalAssocInOldDealer' },
        { label: 'New Associates Count', fieldKey: 'totalAssocInNewDealer' },
        { label: 'Transferred Associates Count', fieldKey: 'totalAssocInBoth' },
        { label: 'Transfer %', fieldKey: 'transferPercentage', isPercent: true, isProgressBar: true },
      ],
      isPaginationRequired: true,
      isSearchRequired: true,
      isSortRequired: true,
      exportConfig: {
        fileName: 'Dealer_Transfer_Report',
        exportMessage: 'Dealer Transfer Report',
        schema: {
          oldDealer: 'Old Dealer',
          newDealer: 'New Dealer',
          totalAssocInOldDealer: 'Old Associates Count',
          totalAssocInNewDealer: 'New Associates Count',
          totalAssocInBoth: 'Transferred Associates Count',
          transferPercentage: 'Transfer %'
        }
      }
    };
    this.apiConfig = {
      path: 'getBuySellDealerSummaryReport',
      method: 'Get',
      urlPath: ApiEnvNames['EliteCertification'],
    };
    this.filterSchema = {
      FieldsArray: [
        createDateField(FieldNames.StartDate, 'Start Date', '01/03/2026'),
        createDateField('endDate', 'End Date', '01/04/2027'),
      ],
      ButtonsArray: [],
      title: '',
      autoSearchOnLoad: false,
      onControlValueChanged: this.onDateChanged,
    };
  }

  ngOnInit(): void {
    if (!this.globalPayloadSubject) {
      this.loadSummary({ startDate: '2026-01-03', endDate: '2027-01-04' });
      return;
    }
    this.globalpayloadSub = this.globalPayloadSubject.subscribe((globalPayload: any) => {
      const body = globalPayload?.Value?.body;
      if (body?.startDate && body?.endDate) {
        const start = new Date(body.startDate);
        const end = new Date(body.endDate);
        if (start <= end) {
          this.loadSummary({ startDate: body.startDate, endDate: body.endDate });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.globalpayloadSub?.unsubscribe();
  }

  onDateChanged = (_event: FieldSelectedValue): void => {
    const fromMonth = this.getSelectedMonth(FieldNames.StartDate);
    const toMonth = this.getSelectedMonth('endDate');
    this.validationMessage = '';
    if (!fromMonth || !toMonth) {
      this.validationMessage = 'Start Date and End Date are required.';
      return;
    }
    if (fromMonth > toMonth) {
      this.validationMessage = 'Start Month cannot be later than End Month.';
      return;
    }
    this.loadSummary({ startDate: this.getDateStart(fromMonth), endDate: this.getDateEnd(toMonth) });
  };

  private loadSummary(body: Record<string, string> = {}): void {
    this.isLoading = true;
    this.tableSchema.isLoading = true;
    this.tableSchema = { ...this.tableSchema, dataSource: [] };
    this.commonService.getDataFromDataSource({ ...this.apiConfig, body }, ApiEnvNames['EliteCertification']).then((response) => {
      const responseData = response as any;
      const result = Array.isArray(response.result)
        ? response.result
        : responseData.payload;
      const rows = Array.isArray(result) ? result : [];
      this.transferRows = response.status === API_STATUS.Success || Array.isArray(responseData.payload) ? rows : [];
      this.tableSchema = { ...this.tableSchema, dataSource: this.transferRows, isLoading: false };
      this.chartInfoFields = [
        { fieldName: FieldNames.TotalCount, value: this.transferRows?.length ?? 0, fieldLabel: 'Total Count', calculateBy: '', infoType: INFO_TYPES.Count }
      ];
      this.chartRows = this.toTransferBands(this.transferRows);
    }).catch(() => {
      this.transferRows = [];
      this.tableSchema = { ...this.tableSchema, dataSource: [], isLoading: false };
      this.chartRows = [];
    }).finally(() => {
      this.isLoading = false;
    });
  }

  private getSelectedMonth(fieldName: string): string {
    const selectedValue = this.filterSchema.FieldsArray.find(field => field.fieldName === fieldName)?.selectedValue;
    if (selectedValue instanceof Date) {
      return `${selectedValue.getFullYear()}-${String(selectedValue.getMonth() + 1).padStart(2, '0')}-${String(selectedValue.getDate()).padStart(2, '0')}`;
    }
    const [month, day, year] = String(selectedValue ?? '').split('/');
    return month && day && year ? `${year}-${month}-${day}` : '';
  }

  private getDateStart(date: string): string {
    return date;
  }

  private getDateEnd(date: string): string {
    return date;
  }

  private toTransferBands(rows: TransferRow[]): Array<{ band: string; count: number; dealerDetails: TransferRow[] }> {
    return transferBands.map((band, index) => {
      const bandRows = rows.filter(row => Math.min(Math.floor(Number(row.transferPercentage ?? 0) / 10) * 10, 90) === index * 10);
      return {
        band,
        count: bandRows.length,
        dealerDetails: bandRows.map(row => ({
          ...row,
          dealer: `${row.oldDealer ?? ''} -> ${row.newDealer ?? ''}`,
          name: `${row.oldDealer ?? ''} -> ${row.newDealer ?? ''}`,
          totalAssocInOldDealer: Number(row.totalAssocInOldDealer ?? 0),
          totalAssocInNewDealer: Number(row.totalAssocInNewDealer ?? 0),
          totalAssocInBoth: Number(row.totalAssocInBoth ?? 0),
          transferPercentage: Number(row.transferPercentage ?? 0),
        })),
      };
    });
  }
}